import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { Reveal, BranchDivider } from "@/components/Cinematic";

export default function WorkDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null); setNotFound(false);
    window.scrollTo(0, 0);
    api.get(`/projects/${slug}`).then((r) => {
      setData(r.data);
      document.title = `${r.data.project.title} — seedesign`;
    }).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="pt-40 pb-40 text-center">
      <p className="font-serif text-3xl text-[#3F3F2C]">Project not found.</p>
      <Link to="/work" className="link-underline text-[#66371B] mt-4 inline-block">Back to work</Link>
    </div>
  );
  if (!data) return <div className="pt-40 pb-40 text-center font-serif text-2xl text-[#81754B]">Loading…</div>;

  const p = data.project;
  const gallery = p.final_gallery || [];

  return (
    <article className="pt-24" data-testid="case-study">
      {/* paper-unfold intro */}
      <motion.header initial={{ opacity: 0, rotateX: 12, scale: 0.98 }} animate={{ opacity: 1, rotateX: 0, scale: 1 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} style={{ transformOrigin: "top center" }}
        className="mx-auto max-w-[1100px] px-6 lg:px-10">
        <Link to="/work" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-[#81754B] hover:text-[#66371B]" data-testid="back-to-work">
          <ArrowLeft size={16} /> Work
        </Link>
        <p className="signature text-2xl text-[#81754B] mt-8">{p.industry}</p>
        <h1 className="font-serif text-5xl sm:text-7xl tracking-tight text-[#3F3F2C] mt-2">{p.title}</h1>
        <p className="mt-6 max-w-2xl text-xl text-[#3F3F2C]">{p.subtitle}</p>
        <div className="mt-8 flex flex-wrap gap-x-10 gap-y-3 text-sm">
          {p.client && <div><span className="text-[#81754B]">Client</span><p className="text-[#3F3F2C]">{p.client}</p></div>}
          {p.role && <div><span className="text-[#81754B]">Role</span><p className="text-[#3F3F2C]">{p.role}</p></div>}
          {p.services?.length > 0 && <div><span className="text-[#81754B]">Services</span><p className="text-[#3F3F2C]">{p.services.join(", ")}</p></div>}
        </div>
      </motion.header>

      {/* embedded project gallery — full-width, uncropped, high-res */}
      <div className="mx-auto max-w-[1100px] px-6 lg:px-10 mt-14 space-y-6" data-testid="project-gallery">
        {gallery.map((src, i) => (
          <Reveal key={i} delay={0} className="overflow-hidden rounded-sm bg-[#E3D8C1]">
            <img src={src} alt={`${p.title} — ${i + 1}`} loading={i < 2 ? "eager" : "lazy"}
              className="w-full h-auto block" />
          </Reveal>
        ))}
      </div>

      {p.behance_url && (
        <div className="mx-auto max-w-[1100px] px-6 lg:px-10 mt-10 text-center">
          <a href={p.behance_url} target="_blank" rel="noreferrer" data-testid="view-behance"
            className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] link-underline text-[#66371B]">
            View the full project on Behance <ExternalLink size={15} />
          </a>
        </div>
      )}

      <BranchDivider flowers />

      {/* related + next */}
      {data.related?.length > 0 && (
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 mt-4 mb-24">
          <h2 className="font-serif text-3xl text-[#3F3F2C] mb-8">Related work</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {data.related.map((r) => (
              <Link key={r.slug} to={`/work/${r.slug}`} data-testid={`related-${r.slug}`}
                className="group block overflow-hidden rounded-sm bg-[#E3D8C1]">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={r.hero_image} alt={r.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-4">
                  <h3 className="font-serif text-xl text-[#3F3F2C] group-hover:text-[#66371B] transition-colors">{r.title}</h3>
                </div>
              </Link>
            ))}
          </div>
          <div className="mt-14 rounded-sm bg-[#E3D8C1] p-10 text-center">
            <p className="signature text-3xl text-[#81754B]">Ready to build yours?</p>
            <Link to="/contact" data-testid="case-cta" className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#66371B] px-8 py-4 text-[#F7F1E1] hover:bg-[#3F3F2C] transition-colors">
              Let's create <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      )}
    </article>
  );
}
